import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { estoqueService } from '../../services/api';
import Loading from '../../components/common/Loading';

export default function Estoque() {
  const queryClient = useQueryClient();

  const [tipoConsumo, setTipoConsumo] = useState('oficina');
  const [itemSearch, setItemSearch] = useState('');
  const [modalCadastrarOpen, setModalCadastrarOpen] = useState(false);

  const [novoItem, setNovoItem] = useState({
    nome: '',
    descricao: '',
    valorCompra: 0,
    codigoBarras: '',
    codigoReferencia: '',
    unidade: 'un',
    tipoConsumo: 'oficina',
  });

  const [entrada, setEntrada] = useState({
    itemId: '',
    quantidade: 1,
    valorUnitarioCompra: '',
    dataValidade: '',
    observacoes: '',
  });

  const itensQueryKey = useMemo(
    () => ['estoque-itens', { tipoConsumo, itemSearch }],
    [tipoConsumo, itemSearch]
  );

  const { data: itensResp, isLoading: itensLoading } = useQuery({
    queryKey: itensQueryKey,
    queryFn: () =>
      estoqueService.listarItens({
        page: 1,
        limit: 20,
        tipoConsumo,
        search: itemSearch || undefined,
        ativo: true,
      }),
  });

  const itens = Array.isArray(itensResp?.data?.data) ? itensResp.data.data : [];

  const [itemSelecionadoParaLotes, setItemSelecionadoParaLotes] = useState('');

  const lotesQueryEnabled = !!itemSelecionadoParaLotes;
  const { data: lotesResp, isLoading: lotesLoading } = useQuery({
    queryKey: ['estoque-lotes', { itemId: itemSelecionadoParaLotes }],
    queryFn: () => estoqueService.listarLotes(itemSelecionadoParaLotes, { page: 1, limit: 50 }),
    enabled: lotesQueryEnabled,
  });

  const lotes = lotesResp?.data?.data || [];

  const criarItemMutation = useMutation({
    mutationFn: (dados) => estoqueService.criarItem(dados),
    onSuccess: () => {
      toast.success('Item criado com sucesso');
      queryClient.invalidateQueries({ queryKey: ['estoque-itens'] });
      queryClient.invalidateQueries({ queryKey: ['estoque-lotes'] });
      setNovoItem((prev) => ({
        ...prev,
        nome: '',
        descricao: '',
        valorCompra: 0,
        codigoBarras: '',
        codigoReferencia: '',
        tipoConsumo: tipoConsumo,
      }));
      setModalCadastrarOpen(false);
    },
    onError: (error) => {
      const resp = error?.response?.data;
      const msg = resp?.message;
      const errs = resp?.errors?.map((e) => e.msg).filter(Boolean) || [];
      toast.error(errs.length > 0 ? errs.join(' | ') : (msg || 'Erro ao criar item'));
    },
  });

  const criarEntradaMutation = useMutation({
    mutationFn: (dados) => estoqueService.criarEntrada(dados),
    onSuccess: () => {
      toast.success('Entrada registrada com sucesso');
      queryClient.invalidateQueries(itensQueryKey);
      if (itemSelecionadoParaLotes) {
        queryClient.invalidateQueries(['estoque-lotes', { itemId: itemSelecionadoParaLotes }]);
      }
      setEntrada((prev) => ({
        ...prev,
        quantidade: 1,
        valorUnitarioCompra: '',
        dataValidade: '',
        observacoes: '',
      }));
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message || 'Erro ao registrar entrada');
    },
  });

  if (itensLoading) return <Loading />;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Estoque e Inventário</h1>
          <p className="text-gray-600">Gerencie itens, entradas e lotes por validade</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setTipoConsumo('oficina')}
            className={`px-3 py-2 rounded-md text-sm font-medium border ${
              tipoConsumo === 'oficina'
                ? 'bg-green-600 text-white border-green-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Oficina
          </button>
          <button
            type="button"
            onClick={() => setTipoConsumo('outros')}
            className={`px-3 py-2 rounded-md text-sm font-medium border ${
              tipoConsumo === 'outros'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Outras áreas
          </button>
        </div>
      </div>

      {/* Lista de itens e busca */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between gap-3 mb-3">
          <h3 className="text-lg font-semibold text-gray-900">Itens cadastrados</h3>
          <input
            type="text"
            value={itemSearch}
            onChange={(e) => setItemSearch(e.target.value)}
            placeholder="Buscar por nome, referência ou código de barras..."
            className="w-80 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {itens.length === 0 ? (
          <div className="text-gray-600 text-sm">Nenhum item encontrado para este tipo.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Item</th>
                  <th className="py-2 pr-4">Compra</th>
                  <th className="py-2 pr-4">Códigos</th>
                  <th className="py-2">Ações</th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <tr key={item._id} className="border-b hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium">{item.nome}</td>
                    <td className="py-2 pr-4">{Number(item.valorCompra || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                    <td className="py-2 pr-4 text-gray-600">
                      {item.codigoBarras ? <div>Cód. barras: {item.codigoBarras}</div> : null}
                      {item.codigoReferencia ? <div>Ref: {item.codigoReferencia}</div> : null}
                    </td>
                    <td className="py-2">
                      <button
                        type="button"
                        onClick={() => {
                          setItemSelecionadoParaLotes(item._id);
                          setEntrada((prev) => ({ ...prev, itemId: item._id }));
                        }}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-3 py-1 rounded-md text-sm"
                      >
                        Ver lotes
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Ações */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => {
            setNovoItem((prev) => ({ ...prev, tipoConsumo }));
            setModalCadastrarOpen(true);
          }}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm"
        >
          Cadastrar novo item
        </button>
      </div>

      {/* Modal cadastro de item */}
      {modalCadastrarOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl bg-white rounded-lg shadow-lg border overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Cadastrar novo item</h3>
              <button
                type="button"
                onClick={() => setModalCadastrarOpen(false)}
                className="text-gray-500 hover:text-gray-700 px-2 py-1"
                aria-label="Fechar"
              >
                ✕
              </button>
            </div>

            <div className="px-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
                  <input
                    value={novoItem.nome}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, nome: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valor de compra *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={novoItem.valorCompra}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, valorCompra: parseFloat(e.target.value) }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unidade</label>
                  <input
                    value={novoItem.unidade}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, unidade: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Área / Setor *</label>
                  <select
                    value={novoItem.tipoConsumo}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, tipoConsumo: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="oficina">Oficina</option>
                    <option value="outros">Outras áreas</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código de barras</label>
                  <input
                    value={novoItem.codigoBarras}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, codigoBarras: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Código de referência</label>
                  <input
                    value={novoItem.codigoReferencia}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, codigoReferencia: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="md:col-span-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <input
                    value={novoItem.descricao}
                    onChange={(e) => setNovoItem((prev) => ({ ...prev, descricao: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setModalCadastrarOpen(false)}
                  className="bg-white hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-md text-sm border border-gray-300"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    criarItemMutation.mutate({
                      nome: novoItem.nome,
                      descricao: novoItem.descricao || '',
                      valorCompra: novoItem.valorCompra,
                      codigoBarras: novoItem.codigoBarras || null,
                      codigoReferencia: novoItem.codigoReferencia || null,
                      tipoConsumo: novoItem.tipoConsumo,
                      unidade: novoItem.unidade || 'un',
                    });
                  }}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm"
                  disabled={criarItemMutation.isPending}
                >
                  {criarItemMutation.isPending ? 'Salvando...' : 'Salvar item'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Entradas e lotes */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Entradas (lotes com validade)</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Item *</label>
            <select
              value={entrada.itemId}
              onChange={(e) => setEntrada((prev) => ({ ...prev, itemId: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Selecione um item</option>
              {itens.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.nome}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantidade *</label>
            <input
              type="number"
              min="0.0000001"
              step="0.01"
              value={entrada.quantidade}
              onChange={(e) => setEntrada((prev) => ({ ...prev, quantidade: parseFloat(e.target.value) }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Custo unit. (opcional)</label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={entrada.valorUnitarioCompra}
              onChange={(e) => setEntrada((prev) => ({ ...prev, valorUnitarioCompra: e.target.value }))}
              placeholder="Se vazio, usa valorCompra do item"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Validade (opcional)</label>
            <input
              type="date"
              value={entrada.dataValidade}
              onChange={(e) => setEntrada((prev) => ({ ...prev, dataValidade: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
            <input
              value={entrada.observacoes}
              onChange={(e) => setEntrada((prev) => ({ ...prev, observacoes: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={() => {
              if (!entrada.itemId) {
                toast.error('Selecione um item');
                return;
              }
              criarEntradaMutation.mutate({
                itemId: entrada.itemId,
                quantidade: entrada.quantidade,
                valorUnitarioCompra:
                  entrada.valorUnitarioCompra === '' ? undefined : parseFloat(entrada.valorUnitarioCompra),
                dataValidade: entrada.dataValidade ? entrada.dataValidade : undefined,
                observacoes: entrada.observacoes || '',
              });
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
            disabled={criarEntradaMutation.isPending}
          >
            {criarEntradaMutation.isPending ? 'Salvando...' : 'Registrar entrada'}
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">Lotes do item selecionado</h3>
        {lotesLoading ? (
          <Loading />
        ) : !itemSelecionadoParaLotes ? (
          <div className="text-gray-600 text-sm">Selecione “Ver lotes” em algum item acima.</div>
        ) : lotes.length === 0 ? (
          <div className="text-gray-600 text-sm">Nenhum lote para este item.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-600 border-b">
                  <th className="py-2 pr-4">Entrada</th>
                  <th className="py-2 pr-4">Validade</th>
                  <th className="py-2 pr-4">Qtd disponível</th>
                  <th className="py-2 pr-4">Custo unit.</th>
                </tr>
              </thead>
              <tbody>
                {lotes.map((lote) => (
                  <tr key={lote._id} className="border-b">
                    <td className="py-2 pr-4">{new Date(lote.dataEntrada).toLocaleDateString('pt-BR')}</td>
                    <td className="py-2 pr-4">
                      {lote.dataValidade ? new Date(lote.dataValidade).toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td className="py-2 pr-4 font-medium">{lote.quantidadeDisponivel}</td>
                    <td className="py-2 pr-4">{Number(lote.valorUnitarioCompra || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

